import type { ObjectId } from 'mongodb';

import { QueueStatus } from './Queues';
import { RequirementFlagSettingsType } from './RequirementFlagSettings';
import type { UserWithDate } from './UserTypes';

export enum FlagReason {
  missingDocumentation = 'Missing Documentation',
  illegibleDocumentation = 'Illegible Documentation',
  incorrectDocumentation = 'Incorrect Documentation',
  other = 'Other',
}

/** Used for changedFrom and changedTo in history */
export enum FlagHistoryStatus {
  flagged = 'flagged',
  /**
   * The flag was commented/edited without directly addressing the issue. On partner paths
   * (EPC feedback, broad-section documentId responses via takePartnerFlagAction) this counts
   * as "the partner responded — LightReach review pending".
   */
  modified = 'modified',
  resolved = 'resolved',
  /** The partner uploaded a replacement document or corrected the flagged value. */
  issueAddressed = 'issueAddressed',
  /**
   * An internal reviewer responded, pushing the flag back to the EPC. Consumers treat this as
   * "needs partner action": the portal label falls back to "Not resolved", and partner actions
   * only count if they happen after the latest qcFeedback entry.
   */
  qcFeedback = 'qcFeedback',
}

export enum FlagPurposeType {
  rejection = 'rejection',
  stipulation = 'stipulation',
  additionalReview = 'additionalReview',
  notification = 'notification',
  changeOrder = 'changeOrder',
}

export enum FlagPurposeLabel {
  rejection = 'Rejection',
  stipulation = 'Stipulation',
  additionalReview = 'Additional Review',
  notification = 'Notification (marks as closed)',
  changeOrder = 'Change Order',
}

export enum FlagStatus {
  open = 'open',
  closed = 'closed',
  closedExceptionGranted = 'closedExceptionGranted',
  closedNoIssuesFound = 'closedNoIssuesFound',
  closedDeleted = 'closedDeleted',
  closedDomesticContentOptOut = 'closedDomesticContentOptOut',
}

export const FlagStatusLabels: Record<FlagStatus, string> = {
  [FlagStatus.open]: 'Flag open',
  [FlagStatus.closedNoIssuesFound]: 'Flag closed - no issue found',
  [FlagStatus.closed]: 'Flag closed - issue resolved',
  [FlagStatus.closedExceptionGranted]: 'Flag closed - exception granted',
  [FlagStatus.closedDeleted]: 'Flag closed - deleted',
  [FlagStatus.closedDomesticContentOptOut]: 'Flag closed - domestic content opt out',
};

// Record<FlagPurposeType, string> (not Record<string, string>) so a new FlagPurposeType
// member fails the build until it is given a label — otherwise an unmapped purpose would
// fall through to the raw internal enum string at partner-facing call sites (FIN-4599 review).
export const FlagPurposeToQueueStatusLabelMap: Record<FlagPurposeType, string> = {
  [FlagPurposeType.rejection]: 'Rejections',
  [FlagPurposeType.stipulation]: 'Stipulation',
  [FlagPurposeType.additionalReview]: 'On Hold',
  [FlagPurposeType.notification]: 'Notification',
  [FlagPurposeType.changeOrder]: 'Change Order',
};

export interface FlagProblem {
  id: ObjectId | string;
  displayText: string;
  details?: string[];
  acceptanceCriteriaId: ObjectId | string;
}

export enum FlagSubject {
  dataInput = 'dataInput',
  documentUpload = 'documentUpload',
  none = 'none',
  epcFeedback = 'epcFeedback',
}

export interface ActionPayload {
  flagSubject: FlagSubject;
  actionStatus: FlagHistoryStatus;
  flaggedDocumentId?: string;
  replacementDocumentId?: string;
  changedFromReviewPackageValue?: string;
  changedToReviewPackageValue?: string;
}

export interface FlagActionHistory extends UserWithDate {
  flagSubject: FlagSubject;
  actionStatus: FlagHistoryStatus;
  queueStatus: QueueStatus;
  note?: string;
  fileUpload?: string;
  flaggedDocumentId?: string;
  replacementDocumentId?: string;
  changedFromReviewPackageValue?: string;
  changedToReviewPackageValue?: string;
}

export interface Flag extends UserWithDate {
  id?: ObjectId;
  reason?: FlagReason;
  problems?: FlagProblem[];
  dataKey?: string;
  status?: FlagStatus;
  purposeType?: FlagPurposeType;
  note?: string;
  postReviewNote?: string;
  fileUpload?: string;
  postReviewFileUpload?: string;
  fileName?: string;
  resolved?: boolean;
  baseCategory?: string;
  documentType?: string;
  category?: string;
  type?: string;
  flagType?: RequirementFlagSettingsType;
  action?: ActionPayload;
  actionHistory?: FlagActionHistory[];
  subjectArchivedOrVoided?: boolean;
  fileIds?: string[];
  attachedDocumentIds?: string[];
  checklistItemKey?: string;
  checklistInputKey?: string;
  checklistOptionValue?: string;
  aiCreated?: boolean;
}

export type FlagPayload = Omit<Flag, 'user' | 'date' | 'resolved' | 'actionHistory'>;

export enum FlagResponseType {
  replacementValue = 'replacementValue', // correct a review-package value
  replacementDocumentId = 'replacementDocumentId', // replace the specific flagged document
  documentId = 'documentId', // attach a supporting document to a broad-section flag
}

export const flagTypesNeedingFormField: RequirementFlagSettingsType[] = [
  RequirementFlagSettingsType.batteryMonitoringId,
  RequirementFlagSettingsType.inverterMonitoringId,
  RequirementFlagSettingsType.serialNumber,
  RequirementFlagSettingsType.ahriNumber,
  RequirementFlagSettingsType.systemInstallDateHVAC,
  RequirementFlagSettingsType.systemCostDiscrepancyHVAC,
  RequirementFlagSettingsType.systemCostDiscrepancyWaterHeater,
  RequirementFlagSettingsType.systemInstallDateWaterHeater,
  RequirementFlagSettingsType.systemCostDiscrepancyGenerator,
  RequirementFlagSettingsType.systemInstallDateGenerator,
  RequirementFlagSettingsType.systemCostDiscrepancyGeothermal,
  RequirementFlagSettingsType.systemInstallDateGeothermal,
  RequirementFlagSettingsType.systemMonitoringId,
  RequirementFlagSettingsType.communityName,
];

/** How a stored value is compared to a submitted replacementValue. */
export type FlagValueKind = 'string' | 'date' | 'number';

export interface FlagValueFieldDescriptor {
  path: string;
  kind?: FlagValueKind;
  indexedByDataKey?: boolean;
}

/** Maps a replacementValue flag type to the account field that holds its live value, so the partner can verify the package was already saved with the corrected value. */
export const FLAG_VALUE_FIELD: Partial<Record<RequirementFlagSettingsType, FlagValueFieldDescriptor>> = {
  [RequirementFlagSettingsType.inverterMonitoringId]: { path: 'monitoringSiteId' },
  [RequirementFlagSettingsType.batteryMonitoringId]: { path: 'batteryMonitoringSiteId' },
  [RequirementFlagSettingsType.serialNumber]: { path: 'solarModuleSerialNumber' },
  [RequirementFlagSettingsType.communityName]: { path: 'communityName' },
  [RequirementFlagSettingsType.systemInstallDateHVAC]: { path: 'systemInstallDate.palmetto', kind: 'date' },
  [RequirementFlagSettingsType.systemInstallDateWaterHeater]: { path: 'systemInstallDate.palmetto', kind: 'date' },
  [RequirementFlagSettingsType.systemInstallDateGenerator]: { path: 'systemInstallDate.palmetto', kind: 'date' },
  [RequirementFlagSettingsType.systemInstallDateGeothermal]: { path: 'systemInstallDate.palmetto', kind: 'date' },
  [RequirementFlagSettingsType.systemCostDiscrepancyHVAC]: { path: 'workOrderTotalPrice', kind: 'number' },
  [RequirementFlagSettingsType.ahriNumber]: {
    path: 'systemDesign.systems.$.ahriNumber',
    kind: 'string',
    indexedByDataKey: true,
  },
  [RequirementFlagSettingsType.systemMonitoringId]: {
    path: 'systemDesign.systems.$.monitoringId',
    kind: 'string',
    indexedByDataKey: true,
  },
};

export const flagTypesHaveNoAction: RequirementFlagSettingsType[] = [
  RequirementFlagSettingsType.arbitrageBattery,
  RequirementFlagSettingsType.backupBattery,
  RequirementFlagSettingsType.customerContract,
  RequirementFlagSettingsType.electricalUpgrade,
  RequirementFlagSettingsType.photoVoltaicSystem,
  RequirementFlagSettingsType.siteEligibility,
  RequirementFlagSettingsType.solarReadiness,
  RequirementFlagSettingsType.yearOneProductionEstimate,
  RequirementFlagSettingsType.accessoriesHVAC,
  RequirementFlagSettingsType.equipmentHVAC,
  RequirementFlagSettingsType.zoningSystemHVAC,
  RequirementFlagSettingsType.configurationSettingsBattery,
  RequirementFlagSettingsType.configurationSettingsLayoutInverter,
  RequirementFlagSettingsType.accessToLightReachInverter,
  RequirementFlagSettingsType.accessToLightReachBattery,
  RequirementFlagSettingsType.domesticContentPhotos,
  RequirementFlagSettingsType.existingSystemEnergyBackup,
  RequirementFlagSettingsType.systemDesignIssueEnergyBackup,
  RequirementFlagSettingsType.equipmentWaterHeater,
  RequirementFlagSettingsType.accessoriesWaterHeater,
  RequirementFlagSettingsType.equipmentGenerator,
  RequirementFlagSettingsType.equipmentGeothermal,
  RequirementFlagSettingsType.projectSiteGeothermal,
];

export const getFlagResponseType = (flagType?: RequirementFlagSettingsType): FlagResponseType => {
  if (flagType && flagTypesHaveNoAction.includes(flagType)) return FlagResponseType.documentId;
  if (flagType && flagTypesNeedingFormField.includes(flagType)) return FlagResponseType.replacementValue;
  return FlagResponseType.replacementDocumentId;
};

export const FLAG_RESPONSE_MAPPING: Record<
  FlagResponseType,
  { flagSubject: FlagSubject; actionStatus: FlagHistoryStatus; note?: string }
> = {
  [FlagResponseType.replacementValue]: {
    flagSubject: FlagSubject.dataInput,
    actionStatus: FlagHistoryStatus.issueAddressed,
  },
  [FlagResponseType.replacementDocumentId]: {
    flagSubject: FlagSubject.documentUpload,
    actionStatus: FlagHistoryStatus.issueAddressed,
  },
  [FlagResponseType.documentId]: {
    flagSubject: FlagSubject.epcFeedback,
    actionStatus: FlagHistoryStatus.modified,
    note: 'EPC Feedback',
  },
};

export interface PartnerFlagResponseInput {
  replacementValue?: string;
  replacementDocumentId?: string;
  documentId?: string;
}
